import 'bulma/css/bulma.min.css';
import '../index.css';
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSignOutAlt,
    faArrowRight,
    faShieldAlt,
    faUser,
    faHashtag,
    faCalendarPlus,
    faLock,
    faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../js/hooks/useAuth';
import { signOutUser } from '../js/firebase/config';
import httpRequest from '../js/utils/httpRequest';
import generateUserAlias from '../js/utils/UserAliasGenerator';
import holorayLogo from '../assets/images/png/HoloRay_Logo_256.png';
import joinInfographic from '../assets/images/infographic.png';

export default function Home() {
    const [userAlias, setUserAlias] = useState("");
    const [channelId, setChannelId] = useState("");
    const [isCreating, setIsCreating] = useState(false);
    const [nameError, setNameError] = useState(false);
    const [sessionError, setSessionError] = useState(false);
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        setUserAlias(localStorage.getItem('userAlias') || generateUserAlias());
    }, []);

    const handleLogout = async () => {
        try {
            await signOutUser();
            navigate('/login');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    function validateAndJoin() {
        let valid = true;
        if (!userAlias.trim()) { setNameError(true); valid = false; } else setNameError(false);
        if (!channelId.trim()) { setSessionError(true); valid = false; } else setSessionError(false);
        if (!valid) return;
        localStorage.setItem('userAlias', userAlias.trim());
        navigate(`/channel/${channelId.trim()}`);
    }

    function onCreateChannelClick() {
        if (!userAlias.trim()) { setNameError(true); return; }
        setNameError(false);
        setIsCreating(true);
        localStorage.setItem('userAlias', userAlias.trim());
        httpRequest.getNewChannel(id => {
            setIsCreating(false);
            navigate(`/channel/${id}`);
        });
    }

    return (
        <div className="hxh-root">
            <header className="hxh-topbar">
                <div className="hxh-brand">
                    <img src={holorayLogo} alt="HoloRay" className="hxh-brand-logo" />
                    <span className="hxh-brand-name">HoloXR <span className="hxh-brand-light">Light</span></span>
                </div>
                <div className="hxh-topbar-right">
                    <div className="hxh-hipaa-badge">
                        <FontAwesomeIcon icon={faShieldAlt} />
                        <span>HIPAA Compliant</span>
                    </div>
                    {user && (
                        <button className="hxh-signout-btn" onClick={handleLogout} title="Sign out">
                            <FontAwesomeIcon icon={faSignOutAlt} />
                            <span>Sign out</span>
                        </button>
                    )}
                </div>
            </header>

            {/* Two separate columns: illustration left, join panel right */}
            <main className="hxh-main">
                <section className="hxh-hero">
                    <h1 className="hxh-headline">
                        Protect Life, From <span className="hxh-brand-light light-bold">Anywhere</span>
                    </h1>
                    <p className="hxh-hero-copy">
                        Connect with specialists in real time for remote consults, share diagnostic
                        imaging instantly, and bring the right experts into every case — all within
                        an encrypted, HIPAA-ready session.
                    </p>

                    <div className="hxh-illustration">
                        <img
                            src={joinInfographic}
                            alt="Secure remote medical collaboration"
                            className="hxh-illustration-img"
                        />
                    </div>

                    <div className="hxh-feature-row">
                        <div className="hxh-feature">
                            <span className="hxh-feature-icon"><FontAwesomeIcon icon={faLock} /></span>
                            <span>End-to-end encrypted</span>
                        </div>
                        <div className="hxh-feature">
                            <span className="hxh-feature-icon"><FontAwesomeIcon icon={faUsers} /></span>
                            <span>Multi-expert sessions</span>
                        </div>
                        <div className="hxh-feature">
                            <span className="hxh-feature-icon"><FontAwesomeIcon icon={faShieldAlt} /></span>
                            <span>HIPAA-ready controls</span>
                        </div>
                    </div>
                </section>

                <section className="hxh-card-wrap">
                    <div className="hxh-card">
                        <div className="hxh-card-header">
                            <h2 className="hxh-card-title">Join a Session</h2>
                            <p className="hxh-card-sub">Enter your details to join a live collaboration room.</p>
                        </div>

                        <div className="hxh-field">
                            <label className="hxh-label">Your Name</label>
                            <div className={`hxh-input-wrap ${nameError ? 'error' : ''}`}>
                                <FontAwesomeIcon icon={faUser} className="hxh-input-icon" />
                                <input
                                    className="hxh-input"
                                    type="text"
                                    placeholder="Enter your name"
                                    value={userAlias}
                                    onChange={e => { setUserAlias(e.target.value); setNameError(false); }}
                                    autoComplete="name"
                                />
                            </div>
                            {nameError && <span className="hxh-error-msg">Please enter your name</span>}
                        </div>

                        <div className="hxh-field">
                            <label className="hxh-label">Session ID</label>
                            <div className={`hxh-input-wrap ${sessionError ? 'error' : ''}`}>
                                <FontAwesomeIcon icon={faHashtag} className="hxh-input-icon" />
                                <input
                                    className="hxh-input"
                                    type="text"
                                    placeholder="Enter session ID"
                                    value={channelId}
                                    onChange={e => { setChannelId(e.target.value); setSessionError(false); }}
                                    onKeyDown={e => e.key === 'Enter' && validateAndJoin()}
                                />
                            </div>
                            {sessionError && <span className="hxh-error-msg">Please enter a session ID</span>}
                        </div>

                        <button className="hxh-join-btn" onClick={validateAndJoin}>
                            <span>Join Session</span>
                            <FontAwesomeIcon icon={faArrowRight} />
                        </button>

                        <div className="hxh-or">
                            <span>or</span>
                        </div>

                        <button
                            className="hxh-create-btn"
                            onClick={onCreateChannelClick}
                            disabled={isCreating}
                        >
                            <FontAwesomeIcon icon={faCalendarPlus} />
                            {isCreating ? 'Creating…' : 'Schedule a Session'}
                        </button>

                        <p className="hxh-privacy-note">
                            <FontAwesomeIcon icon={faShieldAlt} className="hxh-privacy-icon" />
                            Sessions are encrypted and not recorded without consent
                        </p>
                    </div>
                </section>
            </main>

            <style>{`
                .hxh-root {
                    height: 100vh;
                    height: 100dvh;
                    max-height: 100dvh;
                    overflow: hidden;
                    background: linear-gradient(180deg, var(--hx-bg) 0%, var(--hx-bg-2) 100%);
                    display: flex;
                    flex-direction: column;
                    font-family: var(--hx-font);
                    color: var(--hx-ink);
                }

                .hxh-topbar {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-shrink: 0;
                    padding: 0.75rem 2rem;
                    padding-left: max(2rem, env(safe-area-inset-left, 0px));
                    padding-right: max(2rem, env(safe-area-inset-right, 0px));
                    padding-top: max(0.75rem, env(safe-area-inset-top, 0px));
                    background: rgba(255, 255, 255, 0.7);
                    backdrop-filter: blur(12px);
                    border-bottom: 1px solid var(--hx-line);
                    z-index: 100;
                }

                .hxh-brand {
                    display: flex;
                    align-items: center;
                    gap: 0.625rem;
                }

                .hxh-brand-logo {
                    height: 28px;
                    width: auto;
                }

                .hxh-brand-name {
                    font-size: 1.05rem;
                    font-weight: 700;
                    color: var(--hx-ink);
                    letter-spacing: -0.01em;
                }

                .hxh-brand-light {
                    color: var(--hx-blue);
                    font-weight: 600;
                }

                .light-bold {
                    font-weight: 800;
                }

                .hxh-topbar-right {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                }

                .hxh-hipaa-badge {
                    display: flex;
                    align-items: center;
                    gap: 0.4375rem;
                    background: var(--hx-blue-soft);
                    border: 1px solid var(--hx-blue-border);
                    color: var(--hx-blue);
                    border-radius: 999px;
                    padding: 0.4rem 0.875rem;
                    font-size: 0.8rem;
                    font-weight: 600;
                }

                .hxh-signout-btn {
                    display: flex;
                    align-items: center;
                    gap: 0.375rem;
                    background: transparent;
                    border: none;
                    color: var(--hx-muted);
                    font-size: 0.8rem;
                    font-weight: 500;
                    padding: 0.4rem 0.5rem;
                    cursor: pointer;
                    transition: color 0.15s;
                    font-family: inherit;
                }

                .hxh-signout-btn:hover {
                    color: var(--hx-ink);
                }

                .hxh-main {
                    flex: 1;
                    min-height: 0;
                    display: grid;
                    grid-template-columns: minmax(0, 1.35fr) minmax(340px, 400px);
                    align-items: center;
                    gap: clamp(1.5rem, 3vw, 2.75rem);
                    max-width: 1280px;
                    width: 100%;
                    margin: 0 auto;
                    padding: clamp(0.75rem, 2vh, 1.75rem) clamp(1.25rem, 3vw, 2.5rem);
                    padding-bottom: max(clamp(0.75rem, 2vh, 1.75rem), env(safe-area-inset-bottom, 0px));
                    overflow: hidden;
                }

                .hxh-hero {
                    min-width: 0;
                    min-height: 0;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                }

                .hxh-headline {
                    font-size: clamp(1.45rem, 2.2vw + 0.5rem, 2.1rem);
                    font-weight: 800;
                    line-height: 1.15;
                    color: var(--hx-ink);
                    margin: 0 0 0.45rem;
                    letter-spacing: -0.02em;
                    flex-shrink: 0;
                }

                .hxh-hero-copy {
                    font-size: clamp(0.82rem, 0.35vw + 0.72rem, 0.92rem);
                    color: var(--hx-muted);
                    line-height: 1.45;
                    margin: 0 0 clamp(0.5rem, 1vh, 0.85rem);
                    flex-shrink: 0;
                    max-width: 34rem;
                }

                .hxh-illustration {
                    position: relative;
                    width: 100%;
                    min-width: 0;
                    flex: 1 1 auto;
                    min-height: clamp(260px, 46vh, 520px);
                    max-height: min(640px, 64vh);
                    margin: 0 0 clamp(0.5rem, 1vh, 0.85rem);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: hidden;
                }

                .hxh-illustration-img {
                    display: block;
                    width: 100%;
                    max-width: 100%;
                    min-width: 0;
                    height: 100%;
                    object-fit: contain;
                    object-position: center center;
                    border-radius: var(--hx-radius);
                    user-select: none;
                    pointer-events: none;
                }

                .hxh-feature-row {
                    display: flex;
                    flex-wrap: wrap;
                    gap: clamp(0.75rem, 1.5vw, 1.25rem);
                    flex-shrink: 0;
                }

                .hxh-feature {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-size: 0.8rem;
                    font-weight: 500;
                    color: var(--hx-ink-2);
                }

                .hxh-feature-icon {
                    width: 26px;
                    height: 26px;
                    border-radius: 7px;
                    background: var(--hx-blue-soft);
                    color: var(--hx-blue);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.72rem;
                    flex-shrink: 0;
                }

                .hxh-card-wrap {
                    position: relative;
                    z-index: 1;
                    width: 100%;
                    min-height: 0;
                    align-self: center;
                }

                .hxh-card {
                    background: var(--hx-card);
                    border: 1px solid var(--hx-line);
                    border-radius: var(--hx-radius);
                    padding: clamp(1.15rem, 2.2vh, 1.65rem) clamp(1.15rem, 2vw, 1.75rem);
                    box-shadow: var(--hx-shadow);
                }

                .hxh-card-header {
                    margin-bottom: clamp(0.85rem, 1.8vh, 1.25rem);
                }

                .hxh-card-title {
                    font-size: 1.2rem;
                    font-weight: 700;
                    color: var(--hx-ink);
                    margin: 0 0 0.3rem;
                    letter-spacing: -0.01em;
                }

                .hxh-card-sub {
                    font-size: 0.82rem;
                    color: var(--hx-muted);
                    margin: 0;
                    line-height: 1.45;
                }

                .hxh-field {
                    margin-bottom: 0.85rem;
                }

                .hxh-label {
                    display: block;
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: var(--hx-ink-2);
                    margin-bottom: 0.35rem;
                }

                .hxh-input-wrap {
                    display: flex;
                    align-items: center;
                    background: var(--hx-line-2);
                    border: 1.5px solid var(--hx-line);
                    border-radius: var(--hx-radius-sm);
                    padding: 0 0.75rem;
                    transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
                }

                .hxh-input-wrap:focus-within {
                    border-color: var(--hx-blue);
                    background: #ffffff;
                    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
                }

                .hxh-input-wrap.error {
                    border-color: var(--hx-danger);
                }

                .hxh-input-icon {
                    color: var(--hx-muted-2);
                    font-size: 0.8rem;
                    flex-shrink: 0;
                    margin-right: 0.55rem;
                }

                .hxh-input {
                    flex: 1;
                    min-width: 0;
                    background: transparent;
                    border: none;
                    outline: none;
                    padding: 0.6rem 0;
                    font-size: 0.9rem;
                    color: var(--hx-ink);
                    font-family: inherit;
                }

                .hxh-input::placeholder {
                    color: var(--hx-muted-2);
                }

                .hxh-error-msg {
                    display: block;
                    font-size: 0.72rem;
                    color: var(--hx-danger);
                    margin-top: 0.3rem;
                    padding-left: 0.25rem;
                }

                .hxh-join-btn {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    background: var(--hx-blue);
                    border: none;
                    border-radius: var(--hx-radius-sm);
                    color: white;
                    font-size: 0.9rem;
                    font-weight: 600;
                    padding: 0.7rem;
                    cursor: pointer;
                    transition: all 0.15s;
                    margin-top: 0.35rem;
                    font-family: inherit;
                    letter-spacing: 0.01em;
                }

                .hxh-join-btn:hover {
                    background: var(--hx-blue-hover);
                    transform: translateY(-1px);
                    box-shadow: 0 8px 20px rgba(37, 99, 235, 0.28);
                }

                .hxh-join-btn:active {
                    transform: translateY(0);
                }

                .hxh-or {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    color: var(--hx-muted-2);
                    font-size: 0.75rem;
                    margin: 0.75rem 0;
                }

                .hxh-or::before,
                .hxh-or::after {
                    content: '';
                    flex: 1;
                    height: 1px;
                    background: var(--hx-line);
                }

                .hxh-create-btn {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    background: #ffffff;
                    border: 1.5px solid var(--hx-blue-border);
                    border-radius: var(--hx-radius-sm);
                    color: var(--hx-blue);
                    font-size: 0.85rem;
                    font-weight: 600;
                    padding: 0.65rem;
                    cursor: pointer;
                    transition: all 0.15s;
                    font-family: inherit;
                    margin-bottom: 0.85rem;
                }

                .hxh-create-btn:hover:not(:disabled) {
                    background: var(--hx-blue-soft);
                    border-color: var(--hx-blue);
                }

                .hxh-create-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .hxh-privacy-note {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.4rem;
                    text-align: center;
                    font-size: 0.72rem;
                    color: var(--hx-muted);
                    margin: 0;
                }

                .hxh-privacy-icon {
                    color: var(--hx-success);
                }

                @media (max-height: 760px) {
                    .hxh-illustration {
                        min-height: 220px;
                        max-height: min(420px, 50vh);
                    }
                    .hxh-hero-copy {
                        display: -webkit-box;
                        -webkit-line-clamp: 2;
                        -webkit-box-orient: vertical;
                        overflow: hidden;
                    }
                }

                @media (max-height: 620px) {
                    .hxh-feature-row {
                        display: none;
                    }
                    .hxh-illustration {
                        min-height: 180px;
                        max-height: min(320px, 45vh);
                    }
                }

                @media (max-width: 1100px) {
                    .hxh-root {
                        height: 100%;
                        min-height: 100%;
                        max-height: none;
                        overflow-x: hidden;
                        overflow-y: auto;
                        -webkit-overflow-scrolling: touch;
                        overscroll-behavior-y: contain;
                    }

                    .hxh-main {
                        flex: 0 0 auto;
                        min-height: auto;
                        display: flex;
                        flex-direction: column;
                        justify-content: flex-start;
                        padding: 1rem 1.25rem calc(1rem + env(safe-area-inset-bottom, 0px));
                        gap: 1rem;
                        overflow: visible;
                    }

                    .hxh-hero {
                        width: 100%;
                        min-height: auto;
                        max-width: 760px;
                        margin: 0 auto;
                        text-align: center;
                        height: auto;
                        flex: 0 0 auto;
                    }

                    .hxh-headline {
                        font-size: clamp(1.35rem, 5vw, 1.75rem);
                        margin-bottom: 0.35rem;
                    }

                    .hxh-hero-copy {
                        font-size: 0.85rem;
                        margin: 0 auto 0.75rem;
                        display: -webkit-box;
                        -webkit-line-clamp: 2;
                        -webkit-box-orient: vertical;
                        overflow: hidden;
                    }

                    .hxh-illustration {
                        min-height: 180px;
                        max-height: min(280px, 36vh);
                        margin: 0 auto 0.75rem;
                    }

                    .hxh-feature-row {
                        display: none;
                    }

                    .hxh-card-wrap {
                        flex: 0 0 auto;
                        min-height: auto;
                        max-width: 440px;
                        width: 100%;
                        margin: 0 auto;
                    }
                }

                @media (max-width: 480px) {
                    .hxh-topbar {
                        padding: 0.65rem 0.85rem;
                        padding-top: calc(0.65rem + env(safe-area-inset-top, 0px));
                        padding-left: max(0.85rem, env(safe-area-inset-left, 0px));
                        padding-right: max(0.85rem, env(safe-area-inset-right, 0px));
                    }

                    .hxh-main {
                        padding: 0.8rem 0.75rem calc(1.25rem + env(safe-area-inset-bottom, 0px));
                    }

                    .hxh-brand-name {
                        font-size: 0.95rem;
                    }

                    .hxh-hipaa-badge span {
                        display: none;
                    }

                    .hxh-signout-btn span {
                        display: none;
                    }

                    .hxh-card {
                        padding: 1.1rem;
                    }

                    .hxh-hero-copy {
                        display: none;
                    }
                }
            `}</style>
        </div>
    );
}
